import { BaseApiClient, ApiResponse } from './base.client';

export interface ShowListRecord {
  id: string;
  mongoId?: string;
  slug: string;
  name: string;
  category: string;
  description: string | null;
  tagLine: string | null;
  image: string | null;
  rating: number | null;
  reviewCount: number | null;
  price: number;
  currency: string;
  currencySymbol: string;
  discountPercent: number | null;
  offerLabel: string | null;
  venueName: string | null;
  city: string | null;
  state: string | null;
  startDate: string;
  endDate: string;
}

export interface ShowsFacetOption {
  value: string;
  count: number;
}

export interface ShowsFilterFacets {
  categories: ShowsFacetOption[];
  ratingThresholds: { value: number; count: number }[];
  priceBounds: { min: number; max: number };
}

export type ShowsSortKey = 'recommended' | 'price' | 'discount' | 'rating' | 'newest';

export interface ShowsSearchResponse {
  records: ShowListRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  facets: ShowsFilterFacets;
  resultsHeading: string;
  sortBy: string;
}

/** Response shape of `POST /initiate-theatre-search` — the legacy CMS's own
 *  `success/message/type/sortApplied/data/pagination/filters/eventListdata` contract, reproduced
 *  verbatim by `ShowsSearchService.initiateTheatreSearch`. `eventListdata` entries are the same
 *  CMS-shaped (PascalCase) per-event blob `ShowsList.tsx`'s `mapCmsEventToRecord` already parses —
 *  left loosely typed here since fully modelling that legacy shape isn't otherwise needed. */
export interface InitiateTheatreSearchResponse {
  success: boolean;
  message: string;
  type: string;
  sortApplied: string;
  data: {
    _id: string;
    city: string;
    country: string;
    destination_type: string;
    country_code: string;
    state: string;
    destId: string;
  };
  pagination: { page: number; limit: number; total: number };
  filters: {
    categories: { categoryName: string; count: number }[];
    ratingFilter: { rating: number; label: string; count: number }[];
    priceFilter: { currency: string; minAvailable: number; maxAvailable: number };
  };
  eventListdata: Record<string, any>[];
}

export interface ShowCitySuggestion {
  id: string;
  city: string;
  state?: string;
  country?: string;
  /** "City, State, Country" — main city name with region/state and country. */
  label: string;
}

export interface ShowNameSuggestion {
  id: string;
  slug: string;
  name: string;
  image: string | null;
  category: string | null;
}

/** Show-name typeahead payload. `hasMore` is true when the query matched
 *  MORE shows than the requested `limit` — the search dropdown renders it
 *  as the "Show all results" row (CMS `theatreLandingPage.js` parity). */
export interface ShowNameSuggestionResult {
  items: ShowNameSuggestion[];
  hasMore: boolean;
}

/** Detail-page-only fields the listing record doesn't carry — see the
 *  service-side `ShowDetailExtras` (shows-service) for the field mapping. */
export interface ShowDetailExtras {
  images: { detail: string | null; gallery: string[] };
  videos: { Url: string }[];
  venue: { latitude: number | null; longitude: number | null; country: string | null; countryCode: string | null };
  runningTime: string | null;
  minimumAge: string | null;
  /** The `Show` document's real Mongo `_id` — use THIS (never `ShowListRecord.id`, which is the LTD
   *  numeric event id) as the booking funnel's `ReserveTicketsBody.eid`. */
  mongoId: string;
  ltdEventUrlId: string | null;
  description: string | null;
  tagLine: string | null;
  importantNotice: string | null;
  accessibility: string | null;
  contentWarnings: string | null;
  seatingPlanSupported: boolean;
  cast: { name: string; image: string | null }[];
  performances: { performanceId: number; dateTime: string; availableTickets: number; minPrice: number; isBookable: boolean }[];
  reviews: { averageRating: number; totalReviews: number; list: { name: string; rating: number; date: string; comment: string }[] } | null;
}

export interface AreaPriceRow {
  areaId: number;
  areaName: string;
  /** Detail-page DISPLAY price — ticket cost + BIP commission incl. VAT.
   *  Excludes the Cost commission, which checkout adds (pricing spec
   *  2026-09-11). */
  priceIncludingCommission: number;
  /** Cost-commission slice checkout will add for this area. */
  transactionFee?: number;
  availableCount: number;
}

/**
 * `price = a*netPrice + b` — see `ShowsPricingService.getLinearCoefficients`.
 *
 * As returned by `GET /performances/pricing-coefficients`, `a`/`b` are the
 * DISPLAY coefficients (ticket cost + BIP commission), because the seat plan
 * is a Detail-page surface and must not show the Cost commission — that is
 * added at checkout (pricing spec 2026-09-11). `checkoutCoefficients` carries
 * the all-in pair for anything that needs to reproduce the charged total.
 */
export interface PricingCoefficients {
  a: number;
  b: number;
  checkoutCoefficients?: { a: number; b: number };
}

/** The one commission transform every seat-plan/checkout surface applies to a vendor NET price —
 *  shared so `LtdSeatPlanWidget`'s canvas price and `SeatSelectionClient`'s sidebar/checkout price
 *  can never independently drift on a future rounding/precision change. */
export function applyPricingCoefficients(coefficients: PricingCoefficients, netPrice: number): number {
  return Math.round((coefficients.a * netPrice + coefficients.b) * 100) / 100;
}

// ─── Booking funnel (reserve → guest-details → payment-intent → confirm-payment → detail) ──────
// Mirrors HotelsClient's own cart/booking-funnel surface — one client owns both search and the
// stateful booking flow (see shows-service's `modules/shows/{ltd,dto,services}/`).

export interface ReserveTicketsBody {
  /** The `Show`'s Mongo `_id` (NOT the LTD numeric event id). */
  eid: string;
  orderId?: string;
  /** LTD performance id — required for the no-seatmap "best seats" mode. */
  pId?: number;
  SeatplanURl?: string;
  /** Auth-session member id, client-supplied (resolve via `authClient.getMe()` before calling —
   *  same pattern as the Sports checkout flow). Stamped onto `Order.memberId` so the booking later
   *  shows up in "My Bookings". Omit for guest checkouts. */
  memberId?: string;
  /** Explicit seat-id mode: `[{Tid}]`. Best-seats mode (requires `pId`): `{ [areaId]: count }`. */
  ticket: Array<{ Tid: number }> | Record<string, number>;
}

export interface GuestDetailsFormData {
  firstName: string;
  lastName: string;
  email: string;
  city: string;
  country: string;
  phone: string;
  address1?: string;
  address2?: string;
  /** The person actually placing the booking — set when "I am booking for someone else" is
   *  selected on Checkout (CMS `theater-checkout.js`'s `booker` object). */
  booker?: {
    firstName?: string;
    lastName?: string;
    isMainGuest?: boolean;
  };
}

export interface GuestDetailsResult {
  isZeroPayment: boolean;
  orderId: string;
}

export interface OrderSummaryTicket {
  TicketId?: number;
  AreaName?: string;
  /** All-in charged price for this ticket. */
  SellingPriceUserCurrency: string;
  finalPriceBeforeConversion: string;
  /** Ticket cost + BIP commission incl. VAT — the figure already shown
   *  on Listing and Detail (pricing spec 2026-09-11). */
  displayPriceUserCurrency?: string;
  /** Cost-commission slice, added at checkout only. */
  transactionFeeUserCurrency?: string;
}

export interface OrderSummaryResponse {
  Tickets: OrderSummaryTicket[];
  MinExpirationDate?: string;
  SeatplanURl?: string;
  TicketsCount: string;
  /** Grand total the customer is charged. */
  converTedPrice: number;
  /**
   * Checkout price breakdown (pricing spec 2026-09-11):
   *   ticketSubtotal      — ticket cost + BIP commission
   *   transactionFeeTotal — Cost commission ("Transaction Fee")
   * The two sum to `converTedPrice`. Optional so a client talking to a
   * pre-split service still compiles — render the total alone then.
   */
  ticketSubtotal?: number;
  transactionFeeTotal?: number;
  isZeroPayment: boolean;
  eventData?: {
    eventId?: number;
    name: string;
    theater: string;
    currency: string;
    currencySymbol: string;
  };
}

export interface ConfirmPaymentBody {
  paymentMethodId: string;
  status: string;
  PaymentStatus?: string;
  PaymentResponse: { id: string };
}

export interface BookingDetailTicket {
  seat: string;
  area: string;
  barcode: string | null;
  barcodeType: 'QRCode' | 'Pdf417' | null;
  priceLabel: string | null;
}

export interface BookingDetailResponse {
  orderId: string;
  bookingReference: string | null;
  reservationId: string | null;
  bookingDate: string | null;
  email: string;
  customer: { fullName: string; email: string };
  showDownloadBtn: boolean;
  event: {
    name: string;
    venueName: string;
    venueCity: string;
    latitude: number | null;
    longitude: number | null;
    performanceDate: string;
    /** `YYYY-MM-DD` for the (first) performance — the machine-readable counterpart to the
     *  human-readable `performanceDate` label. Used as the `dateFrom`/`checkin` for the
     *  confirmation page's nearby cross-sell strips, which all expect ISO dates. `null` for
     *  legacy orders whose stored label can't be parsed. */
    performanceStartDate: string | null;
    runningTime: string | null;
    imageUrl: string | null;
  };
  tickets: BookingDetailTicket[];
  seats: { area: string; seats: string[] }[];
  userCurrency: string;
  /** The all-in amount actually charged — ticket + BIP commission +
   *  transaction fee. This is the figure the confirmation page shows. */
  totalPriceInUserCurrency: number;
  specialNote: string | null;
}

export interface SearchShowsParams {
  q?: string;
  /** Plain substring match against City/Venue.city — the search bar's
   *  second field. Not a ranked typeahead (that endpoint isn't built yet). */
  city?: string;
  /** Comma-separated category names, e.g. "Musical,Play" — union/OR. */
  category?: string;
  /** Comma-separated star thresholds, e.g. "4,3" — union/OR. */
  rating?: string;
  priceMin?: number;
  priceMax?: number;
  dateFrom?: string;
  dateTo?: string;
  today?: boolean;
  weekend?: boolean;
  /** IANA time zone (e.g. "Europe/London") for Today/This Weekend's
   *  "current day" resolution. Defaults server-side to UTC when omitted —
   *  pass `Intl.DateTimeFormat().resolvedOptions().timeZone` from the browser. */
  timezone?: string;
  sortBy?: ShowsSortKey;
  page?: number;
  limit?: number;
}

// ─── Manage-bookings "Shows" tab display types ─────────────────────────────
// These describe Shows/theatre booking DISPLAY data (the manage-bookings list/detail rows), not
// this file's own real booking-funnel types above. Originally served by `sports-events-service`'s
// own `/shows` module (a read-only "my bookings" proxy over the SAME order data the real booking
// funnel — this service — writes, over a second dedicated Mongo connection); moved into THIS
// service (2026-08-26) — see `ShowsClient.getMyTheaterBookings`/`getShowBookingDetail`/
// `downloadShowETicket` below, which now own both the types and the calls.

export interface ShowBookingBasketTicket {
  TicketId: number;
  TicketName1?: string;
  TicketName2?: string;
  Barcode?: string | null;
  BarcodeType?: string;
  // Verbatim CMS/basket doc carries several more legacy fields (FaceValue, SellingPrice,
  // IsRestrictedView, RestrictionDescription, ScheduledOn, ...) not read by this app's UI.
  [key: string]: unknown;
}

export interface ShowBookingBasketItem {
  BasketItemId?: number;
  EventId?: number;
  EventName?: string;
  ImageUrl?: string;
  PerformanceDate?: string;
  AreaName?: string;
  VenueName?: string;
  VenueAddress?: string;
  VenueCity?: string;
  VenuePostCode?: string;
  VenueId?: string | number;
  Seats?: string[];
  Tickets?: ShowBookingBasketTicket[];
  [key: string]: unknown;
}

export interface ShowBookingPaymentDetails {
  currency?: string;
  converTedPrice?: number;
  userCurrency?: string;
  tickets?: Array<{ TicketId: number; SellingPriceUserCurrency?: string | number;[key: string]: unknown }>;
  orderSummaryData?: { Email?: string;[key: string]: unknown };
  [key: string]: unknown;
}

/** One Shows (theatre) order document — manage-bookings "Shows" tab list row. Native replacement
 *  for the legacy CMS `get-my-theater-bookings` partner-API proxy; the actual data comes from
 *  `services/sports-events-service/src/modules/shows/` (see the section doc comment above).
 *  Loosely typed (`[key: string]: unknown`) since the underlying doc is a `strict:false` Mongoose
 *  schema with many legacy/optional fields — only what this app's manage-bookings UI actually
 *  reads is declared; `VenueDetail`/`Image`/`BookingDate`/`TotalPrice`/`EventName`/`tickets`/`Seats`
 *  are added server-side by `ShowsService.decorateListRecord`. */
export interface ShowBooking {
  _id: string;
  Status?: string;
  ConfirmedAt?: string;
  CreatedDate?: string;
  reservationId?: string;
  BookingReference?: string | null;
  performanceDate?: string;
  basketData?: { Items?: ShowBookingBasketItem[];[key: string]: unknown } | null;
  paymentDetails?: ShowBookingPaymentDetails;
  paymentInfo?: string;
  VenueDetail?: string;
  Image?: string;
  BookingDate?: string;
  TotalPrice?: string;
  EventName?: string;
  tickets?: string;
  Seats?: string;
  [key: string]: unknown;
}

/** One row in the manage-bookings "Shows" tab list. `bookingType` is stamped client-side (the
 *  order docs carry no discriminator of their own) — same convention as `SportsBookingListItem`
 *  (`sportsevents.client.ts`). */
export interface ShowsBookingListItem extends ShowBooking {
  bookingType: 'shows';
}

export interface ShowsBookingsListResponse {
  status: number;
  message: string;
  data: {
    Records: ShowBooking[];
    TotalCount: number;
    totalPages: number;
  } | null;
}

export interface ShowBookingDetailTicket {
  area: string;
  TicketId: number;
  seat: string;
  barcode: string | null;
  Price: string;
  barcodeType: string;
}

/** Single Shows booking detail — manage-bookings Shows detail page. Native replacement for the
 *  legacy CMS `get-booking-detail` partner-API proxy; see `ShowsService.buildDetailResponse`. */
export interface ShowBookingDetailData {
  orderId: string;
  bookingReference: string | null;
  reservationId?: string;
  bookingDate: string;
  showBookedDate: string;
  email: string;
  showDownloadBtn: boolean;
  event: { name?: string; image?: string; venue?: string; performanceDate?: string };
  customer: { name: string };
  seats: {
    ticketCount: number;
    ticketCountDisplay: string;
    display: string;
    area?: string;
    viewBookingSeats: string;
    seats?: string;
  };
  tickets: ShowBookingDetailTicket[];
  userCurrency?: string;
  usercurrencySymbol?: string;
  totalPriceInUserCurrency: string;
  theatreName: string;
  theareId?: string;
  eventMinimumAge?: string;
  hasMinimumAge: boolean;
  eventRunningTime: string | null;
  specialNote: string;
  hasSpecialNote: boolean;
  performanceDate?: string;
}

export interface ShowBookingDetailResponse {
  status: number;
  message: string;
  data: ShowBookingDetailData | null;
}

/** One ticket row in the e-ticket-download payload — the client renders each ticket's barcode to
 *  a `<canvas>` itself (qrcode/bwip-js) and sends the resulting PNG up for the PDF template. */
export interface ShowETicketPayload {
  area: string;
  TicketId: number;
  seat: string;
  barcode: string;
  barcodeType: string;
  image: string;
}

// ─── Client ─────────────────────────────────────────────────────────────────

export class ShowsClient extends BaseApiClient {
  constructor(baseUrl?: string) {
    const resolved = baseUrl || process.env.NEXT_PUBLIC_SHOWS_API_URL;
    if (!resolved) {
      throw new Error(
        'ShowsClient: NEXT_PUBLIC_SHOWS_API_URL is not set. ' +
        'Configure it in the .env file (see apps/web/.env.example) or pass an explicit baseUrl to the constructor.',
      );
    }
    super(resolved);
  }

  async search(params?: SearchShowsParams): Promise<ShowsSearchResponse> {
    const res = await this.get<ApiResponse<ShowsSearchResponse>>('/search', params);
    return res.data;
  }

  /**
   * `POST /initiate-theatre-search` — the shows-service's own real, DB-backed re-implementation
   * of the legacy CMS's `initiate-theatre-search.js` (same filter/sort/facet shape, same
   * `success/message/type/sortApplied/data/pagination/filters/eventListdata` response contract —
   * see `ShowsSearchService.initiateTheatreSearch`). This is the endpoint `ShowsList.tsx`'s
   * legacy-shaped client-side search should call directly (this client resolves the real
   * shows-service origin via `NEXT_PUBLIC_SHOWS_API_URL`, same as every other method here) — NOT
   * a same-origin relative `/initiate-theatre-search` fetch, which 404s on any path-based deploy
   * where the CMS (not this Next app) owns the bare root domain, and NOT the unrelated
   * `api-integration-service` REST API.
   */
  async initiateTheatreSearch(payload: Record<string, unknown>, limit = 20): Promise<InitiateTheatreSearchResponse> {
    return this.post<InitiateTheatreSearchResponse>(`/initiate-theatre-search?limit=${limit}`, payload);
  }

  async citySuggestions(q: string, limit = 8): Promise<ShowCitySuggestion[]> {
    const res = await this.get<ApiResponse<ShowCitySuggestion[]>>('/suggestions/cities', { q, limit });
    return res.data;
  }

  async showSuggestions(q: string, limit = 8): Promise<ShowNameSuggestionResult> {
    const res = await this.get<ApiResponse<ShowNameSuggestion[]> & { hasMore?: boolean }>(
      '/suggestions/shows',
      { q, limit },
    );
    return { items: res.data ?? [], hasMore: res.hasMore === true };
  }

  /**
   * Looks up a single show by its listing `id` — there is no dedicated
   * `GET /shows/:id` endpoint yet (only `/search` exists), so this fetches
   * a page comfortably larger than the current catalogue (~55 shows at time
   * of writing) and finds the match client-side. Used by the Show Detail
   * Page to resolve any real listing show, not just the hand-curated mock
   * records in `apps/web/app/shows/_lib/detail/mock-show-detail.ts` — see
   * that file's `resolveMockShowFromPathSegment` doc comment. Returns
   * `null` (not a thrown error) when no show has that id, so callers can
   * fall through to a 404 the same way a missing mock record does.
   */
  async getShowById(id: string): Promise<ShowListRecord | null> {
    const { records } = await this.search({ limit: 500 });
    const directMatch = records.find((r) => r.id === id || r.mongoId === id);
    if (directMatch) return directMatch;

    // If id is a Mongo ObjectId, look up extras to get the LTD slug, then resolve back to listing id
    if (/^[0-9a-f]{24}$/i.test(id)) {
      const extras = await this.getShowDetailExtras(id);
      if (extras?.ltdEventUrlId) {
        const numId = await this.resolveByLtdEventUrlId(extras.ltdEventUrlId);
        if (numId) {
          return records.find((r) => r.id === numId) || null;
        }
      }
    }
    return null;
  }

  /**
   * Detail-page-only extras (image gallery, trailer, venue geo, running
   * time, minimum age) for a single show — `GET /:id`, added alongside
   * `/search`. Returns `null` (not a thrown error) on a 404/unknown id,
   * same "fall through gracefully" contract as `getShowById`.
   */
  /**
   * Reverse-resolves LTD's own `eventUrlId` slug (carried in the real `LTD.Calendar` widget's
   * redirect, `.../booking/{eventUrlId}/{MM-YYYY}/{performanceId}`) back to this show's own
   * listing `id` — see the backend's `ShowsSearchService.resolveByLtdEventUrlId` doc comment for
   * why this needs a reverse lookup at all (LTD's API has no lookup-by-slug endpoint).
   */
  async resolveByLtdEventUrlId(eventUrlId: string): Promise<string | null> {
    const res = await this.get<ApiResponse<{ id: string } | null>>(`/resolve-by-event-url-id/${eventUrlId}`);
    return res.data?.id ?? null;
  }

  async getShowDetailExtras(id: string): Promise<ShowDetailExtras | null> {
    try {
      const res = await this.get<ApiResponse<ShowDetailExtras | null>>(`/${id}`);
      return res.data;
    } catch {
      return null;
    }
  }

  /** Real seating-area prices for the no-seating-plan fallback table — replaces the old
   *  hardcoded `NO_SEATMAP_TICKET_ROWS` fixture. */
  async getPerformanceAreasPrices(performanceId: number | string): Promise<AreaPriceRow[]> {
    const res = await this.get<ApiResponse<AreaPriceRow[]>>(`/performances/${performanceId}/areas-prices`);
    return res.data;
  }

  /** Fetched once per Seat Selection Page mount — see `LtdSeatPlanWidget.tsx`. */
  async getPricingCoefficients(): Promise<PricingCoefficients> {
    const res = await this.get<ApiResponse<PricingCoefficients>>('/performances/pricing-coefficients');
    return res.data;
  }

  // ─── Booking funnel ──────────────────────────────────────────────────────────────────────────

  async reserveTickets(body: ReserveTicketsBody): Promise<{ orderId: string }> {
    const res = await this.post<ApiResponse<{ orderId: string }>>('/reserve', body);
    return res.data;
  }

  async submitGuestDetails(orderId: string, formdata: GuestDetailsFormData): Promise<GuestDetailsResult> {
    const res = await this.post<ApiResponse<GuestDetailsResult>>(`/orders/${orderId}/guest-details`, { formdata });
    return res.data;
  }

  async getOrderSummary(orderId: string, userCurrency?: string): Promise<OrderSummaryResponse> {
    const res = await this.get<ApiResponse<OrderSummaryResponse>>(`/orders/${orderId}/summary`, userCurrency ? { userCurrency } : undefined);
    return res.data;
  }

  async createPaymentIntent(orderId: string): Promise<{ clientSecret: string }> {
    const res = await this.post<ApiResponse<{ clientSecret: string }>>(`/orders/${orderId}/payment-intent`);
    return res.data;
  }

  async confirmPayment(orderId: string, body: ConfirmPaymentBody): Promise<{ BookingReference: string | null }> {
    const res = await this.post<ApiResponse<{ BookingReference: string | null }>>(`/orders/${orderId}/confirm-payment`, body);
    return res.data;
  }

  async getBookingDetail(orderId: string): Promise<BookingDetailResponse> {
    const res = await this.get<ApiResponse<BookingDetailResponse>>(`/orders/${orderId}`);
    return res.data;
  }

  /** Retriable recovery for a booking still stuck on "ticket is being processed" — a single fresh
   *  check (send the real confirmation if barcodes are now ready), safe to call more than once. */
  async checkConfirmation(orderId: string): Promise<{ status: 'sent' | 'already_sent' | 'processing' }> {
    const res = await this.post<ApiResponse<{ status: 'sent' | 'already_sent' | 'processing' }>>(
      `/orders/${orderId}/check-confirmation`,
    );
    return res.data;
  }

  // ─── Manage-bookings "Shows" tab — moved from `SportsEventsClient` (2026-08-26) ────────────────
  // Native replacement for the CMS get-my-theater-bookings / get-booking-detail / download-e-ticket
  // partner-API proxies, backed by THIS service's own `modules/shows/` `MyBookingsController` (see
  // the shared "Manage-bookings 'Shows' tab display types" section above). Previously served by
  // `sports-events-service`'s own `modules/shows/` over a second, dedicated Mongo connection into
  // the SAME order data this service writes; now the read side lives next to the write side.
  /** Manage-bookings "Shows" tab list. Member id is resolved server-side from session cookie,
   *  with client-supplied param as fallback. */
  async getMyTheaterBookings(params: {
    memberId?: string;
    status?: 'active' | 'other';
    page?: number;
    pagesize?: number;
  } = {}): Promise<ShowsBookingsListResponse> {
    return this.get<ShowsBookingsListResponse>('/bookings/list', {
      ...(params.memberId ? { memberId: params.memberId } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.page ? { page: String(params.page) } : {}),
      ...(params.pagesize ? { pagesize: String(params.pagesize) } : {}),
    });
  }

  /** Manage-bookings Shows detail page. */
  async getShowBookingDetail(orderId: string): Promise<ShowBookingDetailResponse> {
    return this.get<ShowBookingDetailResponse>(`/bookings/${orderId}`);
  }

  /** Downloads the e-ticket PDF for a Shows booking. Returns the raw PDF `Blob` — bypasses
   *  `BaseApiClient.post()`/`request()`, which always parses the response as JSON, since this
   *  endpoint responds with `application/pdf`. */
  async downloadShowETicket(body: { cartId: string; tickets: ShowETicketPayload[]; specialNoteMessage?: string }): Promise<Blob> {
    if (!this.baseUrl) {
      throw new Error('ShowsClient: base URL is not configured.');
    }
    const res = await fetch(`${this.baseUrl}/bookings/${body.cartId}/e-ticket`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`Failed to download e-ticket (HTTP ${res.status})`);
    }
    return res.blob();
  }

  /**
   * VIP newsletter subscribe from the confirmation page. Wire contract
   * mirrors `sportsEventsClient.subscribe`: `POST /bookings/subscribe`
   * with `{ email, bookingId? }` -> `{ message }`.
   */
  async subscribe(email: string, bookingId?: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/bookings/subscribe', { email, bookingId });
  }
}

// Client components — singleton export, same convention as
// hotelsClient/restaurantsClient/sportsEventsClient.
export const showsClient = new ShowsClient();
